import { useRef, useCallback } from 'react'
import { Spinner } from '@contentful/f36-spinner'
import Markdown from 'markdown-to-jsx'
import { useNavigate } from 'react-router-dom'

/**
 * Format de date arabe libanais déterministe — « 19 أيار · 14:23 »
 * Évite toute divergence de locale entre navigateurs.
 */
const LEBANESE_MONTHS = [
  'كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران',
  'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول',
]

const formatFlashTime = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const day = d.getDate()
  const month = LEBANESE_MONTHS[d.getMonth()] || ''
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${day} ${month} · ${hh}:${mm}`
}

const Content = ({ entries, onLoadMore, loading, hasMore }) => {
  const observer = useRef()
  const lastEntryRef = useCallback(
    (node) => {
      if (loading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((items) => {
        if (items[0].isIntersecting && hasMore) onLoadMore()
      })
      if (node) observer.current.observe(node)
    },
    [loading, hasMore, onLoadMore],
  )

  const navigate = useNavigate()

  return (
    <>
      {/* Hero — bandeau « مباشر » rouge avec point clignotant */}
      <section className="flash-hero">
        <div className="container container--narrow flash-hero__inner">
          <span className="flash-hero__eyebrow">
            <span className="flash-hero__dot" aria-hidden="true"></span>
            مباشر
          </span>
          <h1 className="flash-hero__title">نشرة الأخبار</h1>
          <p className="flash-hero__subtitle">آخر التطورات في لبنان والعالم</p>
        </div>
      </section>

      {/* Feed */}
      <section className="flash-feed">
        <div className="container container--narrow">
          <h2 className="flash-feed__title">الأخبار</h2>

          {entries.length === 0 ? (
            <p className="flash-empty">لا توجد أخبار حاليًا.</p>
          ) : (
            <ul className="flash-feed__list">
              {entries.map((entry, index) => {
                const truncatedBody =
                  entry.body.split(' ').slice(0, 50).join(' ') + '…'

                return (
                  <li key={entry.slug + index}>
                    <article
                      ref={index === entries.length - 1 ? lastEntryRef : null}
                      className="flash-card"
                    >
                      <p className="flash-card__time">{formatFlashTime(entry.date)}</p>
                      <h3 className="flash-card__title">{entry.title}</h3>
                      <div className="flash-card__body">
                        <Markdown>{truncatedBody}</Markdown>
                      </div>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => navigate(`/post/${entry.slug}`)}
                      >
                        اقرأ المزيد
                      </button>
                    </article>
                  </li>
                )
              })}
            </ul>
          )}

          {loading && (
            <div className="flash-loading flash-loading--inline" role="status">
              <Spinner size="large" />
            </div>
          )}

          {!hasMore && entries.length > 0 && (
            <p className="flash-empty">— انتهت الأخبار المتاحة —</p>
          )}
        </div>
      </section>
    </>
  )
}

export default Content
